import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import CountUp from "react-countup";
import Chart from 'react-apexcharts';
import axios from 'axios';
import { useAuth } from '../../../Provider/AuthProvider';

const MONTHS: string[] = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

type PeriodType = 'all' | 'halfyear' | 'quarter';
type YearType = '2022' | '2023' | '2024' | '2025' | '2026';

// Interface pour les données de l'API
interface MonthData {
  month: string;
  amount: number;
  formatted: string;
  invoice_count: number;
}

interface PeriodData {
  amount: number;
  formatted: string;
  invoice_count: number;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: {
    year: number;
    group_by: string;
    include_month_details: boolean;
    periods: any[];
    statistics: {
      total_year: {
        amount: number;
        formatted: string;
        invoice_count: number;
      };
      average_per_month: number;
      formatted_average_month: string;
      best_month: {
        month: string;
        amount: number;
        formatted: string;
      };
      worst_month: {
        month: string;
        amount: number;
        formatted: string;
      };
    };
    summary: {
      total_periods: number;
      total_months_with_data: number;
    };
  };
  context: any;
}

const Revenue = () => {
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>("all");
  // Obtenir l'année courante
  const currentYear = new Date().getFullYear().toString() as YearType;
  const [selectedYear, setSelectedYear] = useState<YearType>(currentYear);
  const [loading, setLoading] = useState(false);
  const [monthlySales, setMonthlySales] = useState<number[]>(Array(12).fill(0));
  const [invoiceCounts, setInvoiceCounts] = useState<number[]>(Array(12).fill(0));
  const [yearData, setYearData] = useState<ApiResponse['data'] | null>(null);
  
  // États pour la pagination des périodes
  const [quarterIndex, setQuarterIndex] = useState(0);
  const [semesterIndex, setSemesterIndex] = useState(0);
  
  // Définir les trimestres
  const quarters = [
    { name: 'T1 (Jan-Mar)', months: [0, 1, 2] },
    { name: 'T2 (Avr-Juin)', months: [3, 4, 5] },
    { name: 'T3 (Jul-Sep)', months: [6, 7, 8] },
    { name: 'T4 (Oct-Déc)', months: [9, 10, 11] }
  ];
  
  // Définir les semestres
  const semesters = [
    { name: 'S1 (Jan-Juin)', months: [0, 1, 2, 3, 4, 5] },
    { name: 'S2 (Juil-Déc)', months: [6, 7, 8, 9, 10, 11] }
  ];

  // Générer les années de 2022 à l'année courante + 2
  const startYear = 2022;
  const endYear = new Date().getFullYear() ;
  const years: YearType[] = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push(year.toString() as YearType);
  }

  // Fonction pour récupérer les ventes d'une année spécifique avec la nouvelle API
  const fetchYearlySales = async (year: string) => {
    console.log(`🚀 Début chargement pour l'année ${year}`);
    setLoading(true);
    
    try {
      const params = {
        action: 'paidbyyear',
        entity: user.entity,
        year: parseInt(year),
        group_by: 'month',
       
      };

      console.log(`📤 Requête pour ${year}:`, params);

      const response :any = await axios.post<ApiResponse>(
        'https://api.apapiecesauto.fr/apiV2/',
        params,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.token}`,
          }
        }
      );

      console.log(`📥 Réponse pour ${year}:`, response.data);

      if (response.success && response.data) {
        const data = response.data;
        setYearData(data);
        
        // Extraire les données mensuelles
        const monthlyData: number[] = Array(12).fill(0);
        const monthlyCounts: number[] = Array(12).fill(0);
        
        // Les périodes sont les mois car group_by='month'
        data.periods.forEach((period: any) => {
          const monthIndex = period.month.number - 1; // 0-based index
          monthlyData[monthIndex] = period.totals.amount;
          monthlyCounts[monthIndex] = period.totals.invoice_count;
        });
        
        console.log(`📊 Données mensuelles pour ${year}:`, monthlyData);
        setMonthlySales(monthlyData);
        setInvoiceCounts(monthlyCounts);
        
        // Réinitialiser les index lors du changement d'année
        setQuarterIndex(0);
        setSemesterIndex(0);
      } else {
        console.error('Erreur dans la réponse API:', response.data.message);
        setMonthlySales(Array(12).fill(0));
        setInvoiceCounts(Array(12).fill(0));
      }
      
    } catch (error: any) {
      console.error(`❌ Erreur lors du chargement de l'année ${year}:`, error);
      setMonthlySales(Array(12).fill(0));
      setInvoiceCounts(Array(12).fill(0));
    } finally {
      setLoading(false);
      console.log(`✅ Chargement terminé pour ${year}`);
    }
  };

  // Charger les données quand l'année change
  useEffect(() => {
    if (user?.entity) {
      fetchYearlySales(selectedYear);
    }
  }, [selectedYear, user?.entity]);

  // Fonctions de navigation pour les trimestres
  const previousQuarter = () => {
    if (quarterIndex > 0) {
      setQuarterIndex(quarterIndex - 1);
    }
  };

  const nextQuarter = () => {
    if (quarterIndex < quarters.length - 1) {
      setQuarterIndex(quarterIndex + 1);
    }
  };

  // Fonctions de navigation pour les semestres
  const previousSemester = () => {
    if (semesterIndex > 0) {
      setSemesterIndex(semesterIndex - 1);
    }
  };

  const nextSemester = () => {
    if (semesterIndex < semesters.length - 1) {
      setSemesterIndex(semesterIndex + 1);
    }
  };

  // Fonction pour obtenir les données selon la période
  const getFilteredData = (period: PeriodType) => {
    if (period === 'halfyear') {
      const selectedSemester = semesters[semesterIndex];
      return {
        sales: selectedSemester.months.map(monthIndex => monthlySales[monthIndex]),
        months: selectedSemester.months.map(monthIndex => MONTHS[monthIndex]),
        title: semesters[semesterIndex].name
      };
    } else if (period === 'quarter') {
      const selectedQuarter = quarters[quarterIndex];
      return {
        sales: selectedQuarter.months.map(monthIndex => monthlySales[monthIndex]),
        months: selectedQuarter.months.map(monthIndex => MONTHS[monthIndex]),
        title: quarters[quarterIndex].name
      };
    } else {
      return {
        sales: monthlySales,
        months: MONTHS,
        title: `Année ${selectedYear}`
      };
    }
  };

  const filteredData = getFilteredData(selectedPeriod);
  const totalYearSales: number = monthlySales.reduce((sum: number, item: number) => sum + item, 0);
  const averageSales = totalYearSales / 12;
  const bestMonth = Math.max(...monthlySales);
  const monthsWithSales = monthlySales.filter(sale => sale > 0).length;

  // Configuration du graphique à barres
  const chartOptions: any = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: '60%',
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (value: number) => {
        if (value >= 1000000) return `€${(value/1000000).toFixed(1)}M`;
        if (value >= 1000) return `€${(value/1000).toFixed(0)}k`;
        return `€${value.toFixed(0)}`;
      },
      offsetY: -20,
      style: {
        fontSize: '11px',
        colors: ['#304758']
      }
    },
    xaxis: {
      categories: filteredData.months,
      title: {
        text: 'Mois'
      }
    },
    yaxis: {
      title: {
        text: 'Ventes (€)'
      },
      labels: {
        formatter: (value: number) => {
          if (value >= 1000000) return `€${(value/1000000).toFixed(1)}M`;
          if (value >= 1000) return `€${(value/1000).toFixed(0)}k`;
          return `€${value.toFixed(0)}`;
        }
      }
    },
    colors: ['#3b82f6'],
    tooltip: {
      y: {
        formatter: (value: number) => `€${value.toFixed(2)}`
      }
    },
    grid: {
      borderColor: '#f1f1f1'
    },
    title: {
      text: `Ventes ${filteredData.title}`,
      align: 'left',
      style: {
        fontSize: '14px',
        fontWeight: 'bold'
      }
    }
  };

  const series: any = [{
    name: 'Ventes',
    data: filteredData.sales
  }];

  // Formater les montants pour l'affichage
  const formatAmount = (amount: number): string => {
    if (amount >= 1000000) return `€${(amount/1000000).toFixed(2)}M`;
    if (amount >= 1000) return `€${(amount/1000).toFixed(2)}k`;
    return `€${amount.toFixed(2)}`;
  };

  return (
    <React.Fragment>
      <Card>
        <CardHeader className="border-0 align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">Ventes</h4>
          <div className="d-flex gap-2 flex-wrap">
            {/* Sélecteur d'année */}
            <div className="btn-group" role="group">
              {years.map(year => (
                <button
                  key={year}
                  type="button"
                  className={`btn btn-sm ${selectedYear === year ? 'btn-primary' : 'btn-soft-secondary'}`}
                  onClick={() => setSelectedYear(year as YearType)}
                >
                  {year}
                </button>
              ))}
            </div>
            
            {/* Sélecteur de période avec flèches */}
            <div className="d-flex align-items-center gap-1">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${selectedPeriod === 'all' ? 'btn-primary' : 'btn-soft-secondary'}`}
                  onClick={() => setSelectedPeriod('all')}
                >
                  Année
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${selectedPeriod === 'halfyear' ? 'btn-primary' : 'btn-soft-secondary'}`}
                  onClick={() => setSelectedPeriod('halfyear')}
                >
                  Semestre
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${selectedPeriod === 'quarter' ? 'btn-primary' : 'btn-soft-secondary'}`}
                  onClick={() => setSelectedPeriod('quarter')}
                >
                  Trimestre
                </button>
              </div>

              {/* Flèches de navigation pour les trimestres */}
              {selectedPeriod === 'quarter' && (
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className="btn btn-sm btn-soft-secondary"
                    onClick={previousQuarter}
                    disabled={quarterIndex === 0}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  <span className="btn btn-sm btn-light" style={{ cursor: 'default' }}>
                    {quarters[quarterIndex].name}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-soft-secondary"
                    onClick={nextQuarter}
                    disabled={quarterIndex === quarters.length - 1}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}

              {/* Flèches de navigation pour les semestres */}
              {selectedPeriod === 'halfyear' && (
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className="btn btn-sm btn-soft-secondary"
                    onClick={previousSemester}
                    disabled={semesterIndex === 0}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  <span className="btn btn-sm btn-light" style={{ cursor: 'default' }}>
                    {semesters[semesterIndex].name}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-soft-secondary"
                    onClick={nextSemester}
                    disabled={semesterIndex === semesters.length - 1}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>

        <CardHeader className="p-0 border-0 bg-light-subtle">
          <Row className="g-0 text-center">
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                  ) : (
                    <CountUp
                      prefix="€"
                      start={0}
                      end={totalYearSales}
                      duration={3}
                      decimals={2}
                      separator=" "
                    />
                  )}
                </h5>
                <p className="text-muted mb-0">Total {selectedYear}</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                  ) : (
                    <CountUp
                      prefix="€"
                      start={0}
                      end={averageSales}
                      duration={3}
                      decimals={2}
                      separator=" "
                    />
                  )}
                </h5>
                <p className="text-muted mb-0">Moyenne mensuelle</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                  ) : (
                    <CountUp
                      prefix="€"
                      start={0}
                      end={bestMonth}
                      duration={3}
                      decimals={2}
                      separator=" "
                    />
                  )}
                </h5>
                <p className="text-muted mb-0">Meilleur mois</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0 border-end-0">
                <h5 className="mb-1">
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                  ) : (
                    <CountUp
                      end={monthsWithSales}
                      duration={3}
                      suffix="/12"
                    />
                  )}
                </h5>
                <p className="text-muted mb-0">Mois avec ventes</p>
              </div>
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          <div className="w-100">
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2">Chargement des données pour {selectedYear}...</p>
              </div>
            ) : (
              <Chart
                options={chartOptions}
                series={series}
                type="bar"
                height={350}
              />
            )}
          </div>
          
          {/* Tableau récapitulatif */}
          {!loading && selectedPeriod !== 'all' && (
            <div className="mt-3 text-center">
              <small className="text-muted"> 
                Période affichée : <strong>{filteredData.title}</strong>
              </small>
            </div>
          )}
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default Revenue;